import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import useAuth from "../../hooks/useAuth";
import { supabase } from "../../supabase";
import { useHistory, useLocation, Link } from "react-router-dom";
import STLViewer from "../../components/STLViewer";
import "./GalleryWrite.css";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useModal } from "../../context/ModalContext";
import { takeGalleryHandoff } from "../../utils/stlHandoff";

/* 썸네일 배경 = 갤러리 접시 색 (2026-10-16 리뉴얼부터 새 썸네일은 크림색) */
const PLATE_BG = 0xF4E5D6;
/* 갤러리 썸네일·3D 보기 = 크림 배경 위 캐러멜 커터, 확대 (2026-10-16 김현비 — 기존 23개 썸네일과 같은 모양) */
const CUTTER_COLOR = 0xD9A066;
const CUTTER_ZOOM = 1.55;

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/* 파일 칸 하나 (빈 칸 = 점선 드롭존, 채워짐 = 한 줄 요약 + 바꾸기) */
function FileBox({ label, ext, accept, file, existingName, previewUrl, icon, disabled, onPick, t }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  function pick(f) {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(ext)) {
      onPick(null, ext);
      return;
    }
    onPick(f);
  }

  const name = file?.name || existingName;
  const open = () => { if (!disabled) inputRef.current?.click(); };

  return (
    <div
      className={`gw-box ${drag ? "drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (!disabled) pick(e.dataTransfer?.files?.[0]);
      }}
    >
      <div className="gw-box-l">
        <b>{label}</b><small>{ext.replace(".", "").toUpperCase()}</small>
      </div>
      {name ? (
        <div className="gw-fb">
          {previewUrl ? <img src={previewUrl} alt="" /> : <span className="gw-ic">{icon}</span>}
          <div className="gw-fb-t">
            <b title={name}>{name}</b>
            <small>{file ? fmtSize(file.size) : t("galleryUi.existingFile")}</small>
          </div>
          <button type="button" onClick={open} disabled={disabled}>{t("galleryUi.change")}</button>
        </div>
      ) : (
        <button type="button" className="gw-dz" onClick={open} disabled={disabled}>
          <span className="gw-dz-up">↑</span>
          <b>{t("galleryUi.dropText")} <u>{t("galleryUi.dropPick")}</u></b>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        disabled={disabled}
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}

export default function GalleryWrite() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { showAlert } = useModal();
  const history = useHistory();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const editId = query.get("id");
  const fromCutter = query.get("from") === "cutter";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  const [stlFile, setStlFile] = useState(null);
  const [svgFile, setSvgFile] = useState(null);
  const [svgPreview, setSvgPreview] = useState(null);

  const [thumbnailDataUrl, setThumbnailDataUrl] = useState(null);
  const [stlThumbnailReady, setStlThumbnailReady] = useState(false);
  const [existingPost, setExistingPost] = useState(null);
  const [loadingTarget, setLoadingTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const [galleryId] = useState(() => editId || uuidv4());
  const handoffDoneRef = useRef(false);

  const hasKorean = (text) => /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(text || "");

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

  // ✅ AI 메타데이터 생성 함수
  const generateMetadataFromSvg = async (file) => {
    const formData = new FormData();
    formData.append("svg", file);
    formData.append("gallery_id", galleryId);

    const res = await fetch(`/ai/generate-metadata`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      let detail = t("gallery.aiMetadataFailed");

      try {
        const errorData = await res.json();
        detail = errorData?.detail || errorData?.message || detail;
      } catch {
        try {
          const text = await res.text();
          if (text) detail = text;
        } catch {
          // ignore parse failure
        }
      }

      throw new Error(detail);
    }

    return await res.json();
  };

  /* ✨ 그림 보고 자동으로 채우기: 제목·설명·태그를 한 번에 (기존 AI 생성 흐름 그대로) */
  const runAiFill = async (file, hasStl) => {
    if (!file) {
      showAlert(t("gallery.selectSvgFirst"));
      return;
    }
    try {
      setLoadingTarget("image");
      const data = await generateMetadataFromSvg(file);
      const aiTags = data.tags || data.keywords || [];

      setTitle(data.title || "");
      setDescription(data.description || "");
      setKeywords(aiTags);
      setKeywordInput("");

      if (data.derived_png_data_url && !hasStl) {
        setThumbnailDataUrl(data.derived_png_data_url);
      }
    } catch (err) {
      console.error(err);
      showAlert(t("gallery.aiMetadataFailed"));
    } finally {
      setLoadingTarget(null);
    }
  };

  /* ===== 비회원은 로그인 화면으로 ===== */
  useEffect(() => {
    if (!authLoading && !user) history.replace("/login");
  }, [authLoading, user, history]);

  /* ===== 수정 모드 ===== */
  useEffect(() => {
    if (!editId) return;

    supabase
      .from("gallery")
      .select("*")
      .eq("id", editId)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setExistingPost(data);
        setTitle(data.title);
        setDescription(data.description || "");
        setSvgPreview(data.svg_url || null);
        setKeywords(data.tags || []);
      });
  }, [editId]);

  /* ===== 쿠키커터 만들기 → [갤러리에 올리기] 로 넘어온 파일 받기 ===== */
  useEffect(() => {
    if (!fromCutter || editId || handoffDoneRef.current) return;
    handoffDoneRef.current = true;

    takeGalleryHandoff()
      .then((h) => {
        if (!h || !h.stlBlob || !h.svgText) return;
        const base = h.baseName || "cookie-cutter";
        const svg = new File([h.svgText], `${base}.svg`, { type: "image/svg+xml" });
        const stl = new File([h.stlBlob], `${base}.stl`, { type: "model/stl" });

        setSvgFile(svg);
        setSvgPreview(URL.createObjectURL(svg));
        setThumbnailDataUrl(null);
        setStlThumbnailReady(false);
        setStlFile(stl);

        runAiFill(svg, true);
      })
      .catch((err) => console.error("갤러리 올리기 파일 받기 실패:", err));
  }, [fromCutter, editId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 썸네일 캡처용 STL 주소 (렌더마다 새로 만들지 않게)
  const stlObjectUrl = useMemo(
    () => (stlFile ? URL.createObjectURL(stlFile) : null),
    [stlFile]
  );
  useEffect(() => () => { if (stlObjectUrl) URL.revokeObjectURL(stlObjectUrl); }, [stlObjectUrl]);
  const onStlThumbnail = useCallback((dataUrl) => {
    setThumbnailDataUrl(dataUrl);
    setStlThumbnailReady(true);
  }, []);

  /* ===== 키워드 처리 ===== */
  const addKeyword = () => {
    const value = keywordInput.trim();
    if (!value) return;
    if (hasKorean(value)) {
      showAlert(t("gallery.englishOnlyNotice"));
      return;
    }
    if (keywords.includes(value)) return;
    setKeywords((prev) => [...prev, value]);
    setKeywordInput("");
  };

  const removeKeyword = (kw) => {
    setKeywords((prev) => prev.filter((k) => k !== kw));
  };

  function onPickSvg(f, badExt) {
    if (!f) {
      showAlert(t("galleryUi.wrongType", { ext: (badExt || ".svg").toUpperCase() }));
      return;
    }
    setSvgFile(f);
    setSvgPreview(URL.createObjectURL(f));
  }

  function onPickStl(f, badExt) {
    if (!f) {
      showAlert(t("galleryUi.wrongType", { ext: (badExt || ".stl").toUpperCase() }));
      return;
    }
    setThumbnailDataUrl(null);
    setStlThumbnailReady(false);
    setStlFile(f);
  }

  /* ===== 저장 ===== */
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!title) return showAlert(t("gallery.titleRequired"));
    if (keywords.length === 0) return showAlert(t("gallery.tagsRequired"));
    if (!user?.id) return showAlert(t("gallery.loginRequired"));
    if (!svgFile && !existingPost?.svg_url) return showAlert(t("gallery.svgRequired"));
    if (!stlFile && !existingPost?.stl_url) return showAlert(t("gallery.stlRequired"));

    if (hasKorean(title) || hasKorean(description) || keywords.some(hasKorean)) {
      showAlert(t("gallery.englishOnlyNotice"));
      return;
    }

    setSaving(true);

    try {
      let stlUrl = existingPost?.stl_url || null;
      let stlFilename = existingPost?.stl_filename || null;
      let thumbnailUrl = existingPost?.thumbnail_url || null;
      let svgUrl = existingPost?.svg_url || null;

      if (stlFile) {
        const stlExt = stlFile.name.split(".").pop() || "stl";
        const stlPath = `${user.id}/stl_${Date.now()}.${stlExt}`;
        await supabase.storage.from("gallery").upload(stlPath, stlFile);
        stlUrl =
          supabase.storage.from("gallery").getPublicUrl(stlPath).data.publicUrl;
        stlFilename = stlFile.name;
      }

      if (thumbnailDataUrl) {
        const blob = await (await fetch(thumbnailDataUrl)).blob();
        const thumbPath = `${user.id}/thumb_${Date.now()}.png`;
        await supabase.storage.from("gallery").upload(thumbPath, blob);
        thumbnailUrl =
          supabase.storage.from("gallery").getPublicUrl(thumbPath).data.publicUrl;
      }

      if (svgFile) {
        const svgPath = `${user.id}/svg_${Date.now()}.svg`;
        await supabase.storage.from("gallery").upload(svgPath, svgFile);
        svgUrl =
          supabase.storage.from("gallery").getPublicUrl(svgPath).data.publicUrl;
      }

      const payload = {
        title,
        description,
        tags: keywords,
        stl_url: stlUrl,
        stl_filename: stlFilename,
        svg_url: svgUrl,
        thumbnail_url: thumbnailUrl,
      };

      let error;

      if (editId) {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_BASE}/admin/update-gallery-post`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ gallery_id: editId, ...payload }),
        });
        if (!res.ok) {
          const text = await res.text();
          error = { message: text || "수정 실패" };
        }
      } else {
        const { error: insertError } = await supabase
          .from("gallery")
          .insert({
            id: galleryId,
            user_id: user.id,
            price: 0,
            status: user?.role === "admin" ? "approved" : "pending",
            ...payload,
          });

        error = insertError;
      }

      if (error) {
        console.error("GALLERY SAVE ERROR >>>", error);
        showAlert(error.message);
        setSaving(false);
        return;
      }

      if (!editId && user?.role !== "admin") {
        showAlert(t("gallery.pendingNotice") || "관리자 승인 후 갤러리에 공개됩니다.");
      }
      history.push(editId ? `/gallery/${editId}` : "/gallery");


    } catch (err) {
      console.error(err);
      showAlert(t("gallery.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = editId ? t("galleryUi.editTitle") : t("galleryUi.writeTitle");
  const previewThumb = thumbnailDataUrl || existingPost?.thumbnail_url || null;
  const busy = saving || loadingTarget !== null;

  return (
    <div className="gw-page">
      {loadingTarget === "image" && (
        <div className="gw-page-loading" role="status" aria-live="polite">
          <div className="gw-page-loading-card">{t("gallery.aiGenerating")}</div>
        </div>
      )}

      <form className="gw-one" onSubmit={onSubmit}>
        <div className="gw-crumb">
          <Link to="/gallery">{t("galleryUi.crumb")}</Link> › <b>{pageTitle}</b>
        </div>
        <h1>{pageTitle}</h1>
        <p className="gw-sub">{t("galleryUi.writeSub")}</p>

        {/* ① 파일 올리기 */}
        <section className="gw-st">
          <div className="gw-sh">
            <span className="n">1</span><b>{t("galleryUi.step1")}</b>
            <small>{t("galleryUi.step1Note")}</small>
          </div>
          <div className="gw-two">
            <FileBox
              t={t}
              label={t("galleryUi.svgBox")}
              ext=".svg"
              accept=".svg"
              file={svgFile}
              existingName={existingPost?.svg_url ? existingPost.svg_url.split("/").pop() : null}
              previewUrl={svgPreview}
              icon="✎"
              disabled={busy}
              onPick={onPickSvg}
            />
            <FileBox
              t={t}
              label={t("galleryUi.stlBox")}
              ext=".stl"
              accept=".stl"
              file={stlFile}
              existingName={existingPost?.stl_filename || null}
              previewUrl={null}
              icon="⬢"
              disabled={saving}
              onPick={onPickStl}
            />
          </div>
          <div className="gw-from">
            {t("galleryUi.fromCutterPre")}
            <Link to="/cutter">{t("galleryUi.fromCutterLink")}</Link>
            {t("galleryUi.fromCutterPost")}
          </div>
        </section>

        {/* ② 소개 쓰기 */}
        <section className="gw-st">
          <div className="gw-sh">
            <span className="n">2</span><b>{t("galleryUi.step2")}</b>
            <small>{t("galleryUi.step2Note")}</small>
          </div>

          <div className="gw-lab">
            {t("galleryUi.titleLabel")}
            <button
              type="button"
              className="gw-auto"
              disabled={busy || !svgFile}
              onClick={() => runAiFill(svgFile, !!stlFile)}
            >
              {loadingTarget === "image" ? t("gallery.generating") : t("galleryUi.autoFill")}
            </button>
          </div>
          <input
            className="gw-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("gallery.titleInputPlaceholder")}
            disabled={saving}
          />

          <div className="gw-lab">{t("galleryUi.descLabel")}</div>
          <textarea
            className="gw-input gw-ta"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("gallery.descriptionInputPlaceholder")}
            disabled={saving}
          />

          <div className="gw-lab">
            {t("galleryUi.tagsLabel")}<em>{t("galleryUi.tagsHint")}</em>
          </div>
          <div className="gw-tagbox" onClick={(e) => e.currentTarget.querySelector("input")?.focus()}>
            {keywords.map((kw) => (
              <span key={kw} className="gw-tag">
                #{kw}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeKeyword(kw); }}
                  aria-label="remove"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.nativeEvent.isComposing) return;
                  addKeyword();
                }
              }}
              placeholder={t("galleryUi.tagInput")}
              disabled={saving}
            />
          </div>
        </section>

        {/* ③ 확인하고 올리기 */}
        <section className="gw-st">
          <div className="gw-sh">
            <span className="n">3</span><b>{t("galleryUi.step3")}</b>
          </div>
          <div className="gw-pvrow">
            <div className="gw-pvs">
              {previewThumb ? (
                <img src={previewThumb} alt="" />
              ) : (
                stlFile && <span className="gw-pvs-wait">{t("galleryUi.thumbPending")}</span>
              )}
            </div>
            <div className="gw-ok">
              <b>{title || t("galleryUi.untitled")}</b>
              <br />{t("galleryUi.thumbNote")}
              <br />{t("galleryUi.reviewNote")}
            </div>
          </div>
          <button className="gw-submit" disabled={saving}>
            {saving ? t("gallery.saving") : editId ? t("gallery.edit") : t("galleryUi.submit")}
          </button>
        </section>
      </form>

      {/* 썸네일 생성용 (레이아웃 영향 없음) — STL 3D 뷰어 캡쳐, 크림색 접시 배경 */}
      {stlObjectUrl && !stlThumbnailReady && (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: 512,
            height: 512,
          }}
        >
          <STLViewer
            url={stlObjectUrl}
            bgColor={PLATE_BG}
            modelColor={CUTTER_COLOR}
            zoom={CUTTER_ZOOM}
            hideSize
            onThumbnail={onStlThumbnail}
          />
        </div>
      )}
    </div>
  );
}
